import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ToggleLeft,
  ToggleRight,
  Plus,
  Trash2,
  Pencil,
  Save,
  ListTodo,
  Flag,
  RefreshCw,
  AlertTriangle,
  ClipboardList,
  Check,
} from 'lucide-react';

import {
  fetchFeatureFlagsAdminThunk,
  createFeatureFlagThunk,
  updateFeatureFlagThunk,
  deleteFeatureFlagThunk,
} from '../../../redux/featureFlags/featureFlagsActionThunks';
import {
  fetchRoadmapItemsThunk,
  createRoadmapItemThunk,
  updateRoadmapItemThunk,
  deleteRoadmapItemThunk,
} from '../../../redux/admin/roadmapActionThunks';
import { createTaskThunk } from '../../../redux/admin/tasksActionThunks';

// Roadmap severity -> Company Ops task priority, used by the roadmap
// panel's "Create Task" cross-link below (same enum values, different
// table, no shared type -- kept as a plain map rather than importing one
// model's enum into the other's UI).
const SEVERITY_TO_TASK_PRIORITY = { critical: 'critical', high: 'high', medium: 'medium', low: 'low' };

const SEVERITIES = ['critical', 'high', 'medium', 'low'];
const STATUSES = ['open', 'in_progress', 'done'];

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

/**
 * Platform Status — feature flags (runtime on/off toggles, replacing the old hardcoded
 * redux/featureFlags.js) and a live roadmap/todo tracker mirroring Technical_Docs/
 * 08_MASTER_TODO.md, so this becomes an integral, always-current view of what's in the
 * platform and what's still open, rather than a doc only the engineering team reads.
 */
const AdminPlatformStatusComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeView, setActiveView] = useState('flags');

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
  };

  const views = [
    { key: 'flags', label: t('admin.platformStatus.views.flags'), icon: <Flag className="h-4 w-4 mr-2" /> },
    { key: 'roadmap', label: t('admin.platformStatus.views.roadmap'), icon: <ListTodo className="h-4 w-4 mr-2" /> },
  ];

  return (
    <div className="space-y-6">
      <div className={`flex space-x-1 border-b ${themeClasses.border}`}>
        {views.map((v) => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key)}
            className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeView === v.key
                ? `border-blue-600 ${darkMode ? 'text-blue-400 bg-gray-800' : 'text-blue-600 bg-white'}`
                : `border-transparent ${themeClasses.textMuted} ${themeClasses.hoverBg}`
            }`}
          >
            {v.icon}
            {v.label}
          </button>
        ))}
      </div>

      {activeView === 'flags' && <FeatureFlagsPanel themeClasses={themeClasses} darkMode={darkMode} />}
      {activeView === 'roadmap' && <RoadmapPanel themeClasses={themeClasses} darkMode={darkMode} />}
    </div>
  );
};

/* ============================== Feature Flags ============================== */

const FeatureFlagsPanel = ({ themeClasses, darkMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const flags = useSelector((s) => s.featureFlags.adminFlags);
  const loading = useSelector((s) => s.featureFlags.loading.adminFlags);
  const mutating = useSelector((s) => s.featureFlags.loading.mutate);
  const error = useSelector((s) => s.featureFlags.errors.adminFlags);

  const [showCreate, setShowCreate] = useState(false);
  const [newFlag, setNewFlag] = useState({ name: '', description: '' });

  useEffect(() => {
    if (flags.length === 0) dispatch(fetchFeatureFlagsAdminThunk());
  }, [dispatch, flags.length]);

  const handleToggle = (flag) => {
    dispatch(updateFeatureFlagThunk({ name: flag.name, updates: { enabled: !flag.enabled } }));
  };

  const handleDelete = (name) => {
    if (window.confirm(t('admin.platformStatus.flags.deleteConfirm', { name }))) {
      dispatch(deleteFeatureFlagThunk(name));
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newFlag.name.trim()) return;
    dispatch(createFeatureFlagThunk(newFlag)).then((res) => {
      if (!res.error) {
        setNewFlag({ name: '', description: '' });
        setShowCreate(false);
      }
    });
  };

  return (
    <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.platformStatus.flags.heading')}</h2>
          <p className={`text-sm ${themeClasses.textMuted} mt-0.5`}>
            {t('admin.platformStatus.flags.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
          <button
            onClick={() => setShowCreate((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}
          >
            <Plus className="h-4 w-4" /> {t('admin.platformStatus.flags.newFlag')}
          </button>
        </div>
      </div>

      {error && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className={`mb-4 p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
          <input
            type="text"
            placeholder={t('admin.platformStatus.flags.namePlaceholder')}
            value={newFlag.name}
            onChange={(e) => setNewFlag((f) => ({ ...f, name: e.target.value.trim() }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
            required
          />
          <textarea
            placeholder={t('admin.platformStatus.flags.descriptionPlaceholder')}
            value={newFlag.description}
            onChange={(e) => setNewFlag((f) => ({ ...f, description: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`}
            rows={2}
          />
          <div className="flex gap-2">
            <button type="submit" disabled={mutating} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary} disabled:opacity-50`}>
              <Save className="h-4 w-4" /> {t('admin.platformStatus.flags.create')}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonSecondary}`}>
              {t('admin.platformStatus.flags.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {flags.length === 0 && !loading && (
          <p className={`text-sm ${themeClasses.textMuted} py-4 text-center`}>{t('admin.platformStatus.flags.noFlagsYet')}</p>
        )}
        {flags.map((flag) => (
          <div
            key={flag.name}
            className={`flex items-center justify-between gap-4 p-3 rounded-lg border ${themeClasses.border} ${themeClasses.hoverBg} transition-colors`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium font-mono ${themeClasses.text}`}>{flag.name}</span>
              </div>
              {flag.description && (
                <p className={`text-sm ${themeClasses.textMuted} mt-0.5 truncate`}>{flag.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleToggle(flag)}
                disabled={mutating}
                aria-label={flag.enabled ? t('admin.platformStatus.flags.disableAria', { name: flag.name }) : t('admin.platformStatus.flags.enableAria', { name: flag.name })}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${
                  flag.enabled
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {flag.enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                {flag.enabled ? t('admin.platformStatus.flags.enabled') : t('admin.platformStatus.flags.disabled')}
              </button>
              <button
                onClick={() => handleDelete(flag.name)}
                aria-label={t('admin.platformStatus.flags.deleteAria', { name: flag.name })}
                className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================== Roadmap ============================== */

const emptyRoadmapForm = { title: '', description: '', severity: 'medium', status: 'open', category: '', source_doc: '' };

const RoadmapPanel = ({ themeClasses, darkMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const items = useSelector((s) => s.roadmap.items);
  const loading = useSelector((s) => s.roadmap.loading.items);
  const error = useSelector((s) => s.roadmap.errors.items);

  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newItem, setNewItem] = useState(emptyRoadmapForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyRoadmapForm);
  // Transient per-item confirmation after "Create Task" -- not persisted
  // anywhere, just local UI feedback (see the shared-state note on why this
  // doesn't fetch/display a live task count instead).
  const [taskCreatedIds, setTaskCreatedIds] = useState({});

  useEffect(() => {
    dispatch(fetchRoadmapItemsThunk({ status: statusFilter || undefined, severity: severityFilter || undefined }));
  }, [dispatch, statusFilter, severityFilter]);

  const handleCreateTask = (item) => {
    dispatch(createTaskThunk({
      title: `Follow up: ${item.title}`,
      description: item.description || null,
      priority: SEVERITY_TO_TASK_PRIORITY[item.severity] || 'medium',
      assignee_ids: [],
      source_type: 'roadmap',
      source_id: item.id,
    })).then((res) => {
      if (!res.error) setTaskCreatedIds((s) => ({ ...s, [item.id]: true }));
    });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newItem.title.trim()) return;
    dispatch(createRoadmapItemThunk(newItem)).then((res) => {
      if (!res.error) {
        setNewItem(emptyRoadmapForm);
        setShowCreate(false);
      }
    });
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      title: item.title,
      description: item.description || '',
      severity: item.severity,
      status: item.status,
      category: item.category || '',
      source_doc: item.source_doc || '',
    });
  };

  const saveEdit = (itemId) => {
    dispatch(updateRoadmapItemThunk({ itemId, updates: editForm })).then((res) => {
      if (!res.error) setEditingId(null);
    });
  };

  const handleStatusChange = (item, status) => {
    dispatch(updateRoadmapItemThunk({ itemId: item.id, updates: { status } }));
  };

  const handleDelete = (itemId) => {
    if (window.confirm(t('admin.platformStatus.roadmap.deleteConfirm'))) {
      dispatch(deleteRoadmapItemThunk(itemId));
    }
  };

  return (
    <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.platformStatus.roadmap.heading')}</h2>
          <p className={`text-sm ${themeClasses.textMuted} mt-0.5`}>
            {t('admin.platformStatus.roadmap.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
          >
            <option value="">{t('admin.platformStatus.roadmap.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
          >
            <option value="">{t('admin.platformStatus.roadmap.allSeverities')}</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {loading && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
          <button
            onClick={() => setShowCreate((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}
          >
            <Plus className="h-4 w-4" /> {t('admin.platformStatus.roadmap.newItem')}
          </button>
        </div>
      </div>

      {error && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className={`mb-4 p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
          <input
            type="text"
            placeholder={t('admin.platformStatus.roadmap.titlePlaceholder')}
            value={newItem.title}
            onChange={(e) => setNewItem((f) => ({ ...f, title: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
            required
          />
          <textarea
            placeholder={t('admin.platformStatus.roadmap.descriptionPlaceholder')}
            value={newItem.description}
            onChange={(e) => setNewItem((f) => ({ ...f, description: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`}
            rows={2}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <select value={newItem.severity} onChange={(e) => setNewItem((f) => ({ ...f, severity: e.target.value }))}
              className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={newItem.status} onChange={(e) => setNewItem((f) => ({ ...f, status: e.target.value }))}
              className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <input type="text" placeholder={t('admin.platformStatus.roadmap.categoryPlaceholder')} value={newItem.category}
              onChange={(e) => setNewItem((f) => ({ ...f, category: e.target.value }))}
              className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
            <input type="text" placeholder={t('admin.platformStatus.roadmap.sourceDocPlaceholder')} value={newItem.source_doc}
              onChange={(e) => setNewItem((f) => ({ ...f, source_doc: e.target.value }))}
              className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
          </div>
          <div className="flex gap-2">
            <button type="submit" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}>
              <Save className="h-4 w-4" /> {t('admin.platformStatus.roadmap.create')}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonSecondary}`}>
              {t('admin.platformStatus.roadmap.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {items.length === 0 && !loading && (
          <p className={`text-sm ${themeClasses.textMuted} py-4 text-center`}>{t('admin.platformStatus.roadmap.noItemsMatch')}</p>
        )}
        {items.map((item) => {
          const colors = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.medium;
          const isEditing = editingId === item.id;

          if (isEditing) {
            return (
              <div key={item.id} className={`p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
                <input type="text" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
                <textarea value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`} rows={2} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <select value={editForm.severity} onChange={(e) => setEditForm((f) => ({ ...f, severity: e.target.value }))}
                    className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                    className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                  <input type="text" value={editForm.category} onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                    className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
                  <input type="text" value={editForm.source_doc} onChange={(e) => setEditForm((f) => ({ ...f, source_doc: e.target.value }))}
                    className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(item.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}>
                    <Save className="h-4 w-4" /> {t('admin.platformStatus.roadmap.save')}
                  </button>
                  <button onClick={() => setEditingId(null)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonSecondary}`}>
                    {t('admin.platformStatus.roadmap.cancel')}
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div key={item.id} className={`p-4 rounded-lg border ${themeClasses.border} ${themeClasses.hoverBg} transition-colors`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
                      {item.severity}
                    </span>
                    {item.category && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {item.category}
                      </span>
                    )}
                    <span className={`font-medium ${themeClasses.text}`}>{item.title}</span>
                  </div>
                  {item.description && (
                    <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>{item.description}</p>
                  )}
                  {item.source_doc && (
                    <p className={`text-xs ${themeClasses.textMuted} mt-1 font-mono`}>{item.source_doc}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={item.status}
                    onChange={(e) => handleStatusChange(item, e.target.value)}
                    className={`px-2 py-1 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-xs`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                  <button
                    onClick={() => handleCreateTask(item)}
                    disabled={!!taskCreatedIds[item.id]}
                    aria-label={t('admin.platformStatus.roadmap.createTaskAria', { title: item.title })}
                    title={t('admin.platformStatus.roadmap.createTask')}
                    className={`p-1.5 rounded-lg disabled:opacity-50 ${darkMode ? 'text-gray-500 hover:text-indigo-400 hover:bg-indigo-900/20' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                  >
                    {taskCreatedIds[item.id] ? <Check className="h-4 w-4 text-emerald-500" /> : <ClipboardList className="h-4 w-4" />}
                  </button>
                  <button onClick={() => startEdit(item)} aria-label={t('admin.platformStatus.roadmap.editAria', { title: item.title })}
                    className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-blue-400 hover:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} aria-label={t('admin.platformStatus.roadmap.deleteAria', { title: item.title })}
                    className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminPlatformStatusComponent;
